import React from "react";
import {
  Box,
  Typography,
  useTheme,
  CircularProgress,
  Paper,
  Grid,
  Divider,
  Chip,
  Link,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import Sidebar from "../components/sidebar/Sidebar";
import { useSelector } from "react-redux";
import { selectSidebarCollapsed } from "../store/slices/app/appSelector";
import { useGetSystemStatsQuery } from "../store/apis/systemStatsApi";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ApiIcon from "@mui/icons-material/Api";
import PageBreadcrumbs from "../components/layout/PageBreadcrumbs";
import PageContainer from "../components/layout/PageContainer";

const SystemHealth: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const collapsed = useSelector(selectSidebarCollapsed);

  // Get Firebase project ID from environment variables
  const projectId =
    process.env.REACT_APP_FIREBASE_PROJECT_ID || "beapolo-5c159";

  // Get API URLs from environment variables
  const apiBackendUrl = process.env.REACT_APP_API_BACKEND_URL || "";
  const metricsDispatcherUrl =
    process.env.REACT_APP_METRICS_DISPATCHER_URL || "";
  const metricsProcessorUrl = process.env.REACT_APP_METRICS_PROCESSOR_URL || "";

  // Swagger API documentation URLs
  const swaggerServices = [
    {
      name: "Backend API",
      service: "backend-app",
      url: `${apiBackendUrl}/api-docs/`,
      baseUrl: apiBackendUrl,
    },
    {
      name: "Metrics Dispatcher",
      service: "metrics-dispatcher",
      url: `${metricsDispatcherUrl}/api-docs/`,
      baseUrl: metricsDispatcherUrl,
    },
    {
      name: "Metrics Processor",
      service: "metrics-processor",
      url: `${metricsProcessorUrl}/api-docs/`,
      baseUrl: metricsProcessorUrl,
    },
  ];

  // Get current timestamp for logs
  const now = new Date();
  const cursorTimestamp = now.toISOString();

  // Service log URLs
  const logServices = [
    {
      name: "Metrics Processor",
      service: "metrics-processor",
      url: `https://console.cloud.google.com/logs/query;query=logName%3D%22projects%2F${projectId}%2Flogs%2Fmetrics-processor%22;cursorTimestamp=${cursorTimestamp};duration=PT3H?project=${projectId}`,
    },
    {
      name: "Metrics Dispatcher",
      service: "metrics-dispatcher",
      url: `https://console.cloud.google.com/logs/query;query=logName%3D%22projects%2F${projectId}%2Flogs%2Fmetrics-dispatcher%22;cursorTimestamp=${cursorTimestamp};duration=PT3H?project=${projectId}`,
    },
    {
      name: "Application Errors",
      service: "backend-service",
      url: `https://console.cloud.google.com/logs/query;query=logName%3D%22projects%2F${projectId}%2Flogs%2Fbackend-service%22;cursorTimestamp=${cursorTimestamp};duration=PT3H?project=${projectId}`,
    },
  ];

  // Fetch system stats data
  const {
    data: systemStatsData,
    isLoading: isLoadingSystemStats,
    error: systemStatsError,
  } = useGetSystemStatsQuery();

  return (
    <Box sx={{ display: "flex" }}>
      <Sidebar />

      <Box
        sx={{
          flexGrow: 1,
          p: 3,
          transition: "margin-left 0.3s ease",
          marginLeft: collapsed ? "80px" : "250px",
        }}
      >
        <PageBreadcrumbs />

        <PageContainer>
          <Typography
            variant="h4"
            sx={{
              fontFamily: "Nunito",
              fontWeight: 700,
              color: theme.palette.grey[900],
              mb: 3,
            }}
          >
            {t("systemHealth.title", "System Health")}
          </Typography>

          {isLoadingSystemStats ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "50vh",
              }}
            >
              <CircularProgress />
            </Box>
          ) : systemStatsError ? (
            <Box
              sx={{
                p: 3,
                backgroundColor: theme.palette.error.light,
                borderRadius: 1,
              }}
            >
              <Typography color="primary">
                {t(
                  "systemHealth.errorLoading",
                  "Error loading system health data"
                )}
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={3}>
              {/* API Documentation Section */}
              <Grid item xs={12}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: 2,
                    border: `1px solid ${theme.palette.grey[200]}`,
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontFamily: "Nunito",
                      fontWeight: 700,
                      color: theme.palette.grey[900],
                      mb: 2,
                    }}
                  >
                    {t("systemHealth.apiDocs", "API Documentation")}
                  </Typography>
                  <Divider sx={{ mb: 2 }} />

                  <Grid container spacing={2}>
                    {swaggerServices.map((service) => (
                      <Grid item xs={12} sm={6} md={4} key={service.service}>
                        <Box
                          sx={{
                            p: 2,
                            borderRadius: 1,
                            border: `1px solid ${theme.palette.grey[200]}`,
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <ApiIcon color="primary" />
                            <Typography
                              sx={{
                                fontFamily: "Nunito",
                                fontWeight: 700,
                              }}
                            >
                              {service.name}
                            </Typography>
                          </Box>
                          <Typography
                            variant="body2"
                            sx={{
                              color: theme.palette.text.secondary,
                              fontSize: "0.85rem",
                              mb: 1,
                            }}
                          >
                            {service.baseUrl}
                          </Typography>
                          <Link
                            href={service.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                              color: theme.palette.primary.main,
                              textDecoration: "none",
                              "&:hover": { textDecoration: "underline" },
                            }}
                          >
                            {t("systemHealth.viewSwagger", "View Swagger UI")}
                            <OpenInNewIcon sx={{ fontSize: 16 }} />
                          </Link>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Paper>
              </Grid>

              {/* Logs Section */}
              <Grid item xs={12}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: 2,
                    border: `1px solid ${theme.palette.grey[200]}`,
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontFamily: "Nunito",
                      fontWeight: 700,
                      color: theme.palette.grey[900],
                      mb: 2,
                    }}
                  >
                    {t("systemHealth.logs", "System Logs")}
                  </Typography>
                  <Divider sx={{ mb: 2 }} />

                  <Grid container spacing={2}>
                    {logServices.map((logService) => (
                      <Grid item xs={12} sm={6} md={4} key={logService.service}>
                        <Box
                          sx={{
                            p: 2,
                            borderRadius: 1,
                            border: `1px solid ${theme.palette.grey[200]}`,
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                          }}
                        >
                          <Typography
                            sx={{
                              fontFamily: "Nunito",
                              fontWeight: 700,
                            }}
                          >
                            {logService.name}
                          </Typography>
                          <Link
                            href={logService.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                              color: theme.palette.primary.main,
                              textDecoration: "none",
                              "&:hover": { textDecoration: "underline" },
                            }}
                          >
                            {t("systemHealth.viewLogs", "View Logs")}
                            <OpenInNewIcon sx={{ fontSize: 16 }} />
                          </Link>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Paper>
              </Grid>

              {/* Services Status */}
              <Grid item xs={12}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: 2,
                    border: `1px solid ${theme.palette.grey[200]}`,
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontFamily: "Nunito",
                      fontWeight: 700,
                      color: theme.palette.grey[900],
                      mb: 2,
                    }}
                  >
                    {t("systemHealth.services", "Services")}
                  </Typography>
                  <Divider sx={{ mb: 2 }} />

                  {systemStatsData?.data?.services ? (
                    <Grid container spacing={2}>
                      {Object.entries(systemStatsData.data.services).map(
                        ([serviceName, serviceData]) => (
                          <Grid item xs={12} sm={6} md={4} key={serviceName}>
                            <Box
                              sx={{
                                p: 2,
                                borderRadius: 1,
                                border: `1px solid ${theme.palette.grey[200]}`,
                                display: "flex",
                                flexDirection: "column",
                                gap: 1,
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontFamily: "Nunito",
                                    fontWeight: 700,
                                    textTransform: "capitalize",
                                  }}
                                >
                                  {serviceName}
                                </Typography>
                                <Chip
                                  label={serviceData.status}
                                  color={
                                    serviceData.status === "healthy"
                                      ? "success"
                                      : "error"
                                  }
                                  size="small"
                                />
                              </Box>
                              <Typography variant="body2">
                                {t("systemHealth.url")}:{" "}
                                <Link
                                  href={serviceData.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  sx={{
                                    textDecoration: "none",
                                    "&:hover": { textDecoration: "underline" },
                                  }}
                                >
                                  {serviceData.url}
                                </Link>
                              </Typography>
                              <Typography variant="body2">
                                {t("systemHealth.responseTime")}:{" "}
                                {serviceData.responseTime}ms
                              </Typography>
                              <Typography
                                variant="caption"
                                color="textSecondary"
                              >
                                {t("systemHealth.lastChecked")}:{" "}
                                {new Date(
                                  serviceData.timestamp
                                ).toLocaleString()}
                              </Typography>
                            </Box>
                          </Grid>
                        )
                      )}
                    </Grid>
                  ) : (
                    <Typography>
                      {t(
                        "systemHealth.noServicesData",
                        "No services data available"
                      )}
                    </Typography>
                  )}
                </Paper>
              </Grid>

              {/* Database Stats */}
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: 2,
                    border: `1px solid ${theme.palette.grey[200]}`,
                    height: "100%",
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontFamily: "Nunito",
                      fontWeight: 700,
                      color: theme.palette.grey[900],
                      mb: 2,
                    }}
                  >
                    {t("systemHealth.database", "Database")}
                  </Typography>
                  <Divider sx={{ mb: 2 }} />

                  {systemStatsData?.data?.database ? (
                    <Box>
                      <Box sx={{ mb: 3 }}>
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 600, mb: 1 }}
                        >
                          {t("systemHealth.generalInfo", "General Information")}
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={6}>
                            <Typography variant="body2">
                              {t("systemHealth.connections")}:{" "}
                              {systemStatsData.data.database.connectionCount}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="body2">
                              {t("systemHealth.size")}:{" "}
                              {systemStatsData.data.database.size}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Box>

                      <Box sx={{ mb: 3 }}>
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 600, mb: 1 }}
                        >
                          {t("systemHealth.counts", "Record Counts")}
                        </Typography>
                        <Grid container spacing={2}>
                          {Object.entries(
                            systemStatsData.data.database.counts
                          ).map(([name, count]) => (
                            <Grid item xs={4} key={name}>
                              <Box
                                sx={{
                                  p: 1,
                                  textAlign: "center",
                                  borderRadius: 1,
                                  bgcolor: theme.palette.grey[100],
                                }}
                              >
                                <Typography
                                  variant="h6"
                                  sx={{ fontWeight: 700 }}
                                >
                                  {count}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  sx={{ textTransform: "capitalize" }}
                                >
                                  {name}
                                </Typography>
                              </Box>
                            </Grid>
                          ))}
                        </Grid>
                      </Box>

                      <Box>
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 600, mb: 1 }}
                        >
                          {t("systemHealth.tables", "Tables")}
                        </Typography>
                        <Box
                          sx={{
                            maxHeight: 200,
                            overflowY: "auto",
                            border: `1px solid ${theme.palette.grey[200]}`,
                            borderRadius: 1,
                          }}
                        >
                          {systemStatsData.data.database.tables.map((table) => (
                            <Box
                              key={table.name}
                              sx={{
                                p: 1,
                                display: "flex",
                                justifyContent: "space-between",
                                borderBottom: `1px solid ${theme.palette.grey[200]}`,
                                "&:last-child": {
                                  borderBottom: "none",
                                },
                              }}
                            >
                              <Typography variant="body2">
                                {table.name}
                              </Typography>
                              <Typography variant="body2">
                                {table.rowCount}{" "}
                                {t("systemHealth.rows", "rows")}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    </Box>
                  ) : (
                    <Typography>
                      {t(
                        "systemHealth.noDatabaseData",
                        "No database data available"
                      )}
                    </Typography>
                  )}
                </Paper>
              </Grid>

              {/* PubSub Status */}
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: 2,
                    border: `1px solid ${theme.palette.grey[200]}`,
                    height: "100%",
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontFamily: "Nunito",
                      fontWeight: 700,
                      color: theme.palette.grey[900],
                      mb: 2,
                    }}
                  >
                    {t("systemHealth.pubsub", "PubSub")}
                  </Typography>
                  <Divider sx={{ mb: 2 }} />

                  {systemStatsData?.data?.pubsub ? (
                    <Box>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {t("systemHealth.topic")}:{" "}
                          {systemStatsData.data.pubsub.topic}
                        </Typography>
                        <Chip
                          label={
                            systemStatsData.data.pubsub.exists
                              ? t("systemHealth.exists", "Exists")
                              : t("systemHealth.notExists", "Does Not Exist")
                          }
                          color={
                            systemStatsData.data.pubsub.exists
                              ? "success"
                              : "error"
                          }
                          size="small"
                          sx={{ mt: 1 }}
                        />
                      </Box>

                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 600, mb: 1 }}
                      >
                        {t("systemHealth.subscriptions", "Subscriptions")}
                      </Typography>
                      {systemStatsData.data.pubsub.subscriptions.length > 0 ? (
                        <Box
                          sx={{
                            maxHeight: 250,
                            overflowY: "auto",
                            border: `1px solid ${theme.palette.grey[200]}`,
                            borderRadius: 1,
                          }}
                        >
                          {systemStatsData.data.pubsub.subscriptions.map(
                            (subscription, index) => (
                              <Box
                                key={subscription.name}
                                sx={{
                                  p: 1.5,
                                  borderBottom:
                                    index <
                                    (systemStatsData?.data?.pubsub
                                      ?.subscriptions?.length ?? 0) -
                                      1
                                      ? `1px solid ${theme.palette.grey[200]}`
                                      : "none",
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    mb: 1,
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 600 }}
                                  >
                                    {subscription.name}
                                  </Typography>
                                  <Chip
                                    label={
                                      subscription.exists
                                        ? t("systemHealth.active", "Active")
                                        : t("systemHealth.inactive", "Inactive")
                                    }
                                    color={
                                      subscription.exists ? "success" : "error"
                                    }
                                    size="small"
                                  />
                                </Box>
                                {subscription.exists && (
                                  <Box>
                                    <Typography variant="body2">
                                      {t("systemHealth.messageCount")}:{" "}
                                      {subscription.messageCount || 0}
                                    </Typography>
                                    <Typography variant="body2">
                                      {t("systemHealth.retentionDuration")}:{" "}
                                      {subscription.retentionDuration || "N/A"}
                                    </Typography>
                                    {subscription.oldestMessage && (
                                      <Typography variant="body2">
                                        {t("systemHealth.oldestMessage")}:{" "}
                                        {new Date(
                                          subscription.oldestMessage
                                        ).toLocaleString()}
                                      </Typography>
                                    )}
                                  </Box>
                                )}
                              </Box>
                            )
                          )}
                        </Box>
                      ) : (
                        <Typography variant="body2">
                          {t(
                            "systemHealth.noSubscriptions",
                            "No subscriptions available"
                          )}
                        </Typography>
                      )}
                    </Box>
                  ) : (
                    <Typography>
                      {t(
                        "systemHealth.noPubSubData",
                        "No PubSub data available"
                      )}
                    </Typography>
                  )}
                </Paper>
              </Grid>
            </Grid>
          )}
        </PageContainer>
      </Box>
    </Box>
  );
};

export default SystemHealth;
